import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faHandshake, faEye, faEyeSlash, faRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { Auth } from '../../../../core/auth/auth';
import { PopupInfoService } from '../../../../shared/services/popup-info.service';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, FontAwesomeModule],
  templateUrl: './login-page.html',
  styleUrl: './login-page.scss',
})
export class LoginPage implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(Auth);
  private readonly router = inject(Router);
  private readonly popupInfoService = inject(PopupInfoService);
  private readonly activatedRoute = inject(ActivatedRoute);

  currentYear = new Date().getFullYear();

  // FontAwesome icons
  faHandshake = faHandshake;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  faMicrosoft = faMicrosoft;
  faRotateLeft = faRotateLeft;

  isLoading = false;
  errorMessage = '';
  // Forgot password modal state
  showForgot = false;
  isSendingForgot = false;
  forgotMessage = '';

  // Login Microsoft
  isProcessingMicrosoftLogin = false;
  isMicrosoftLoginLoading = false;

  // Password visibility toggle for the password field
  passwordVisible = false;

  forgotForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
  });

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      const accessToken = params['access_token'];
      const reason = params['reason'];
      if (accessToken) {
        this.isProcessingMicrosoftLogin = true;
        if (accessToken === 'error') {
          let msg = 'Lo sentimos, no se pudo iniciar sesión con Microsoft.';
          if (!reason) {
            msg += ' El usuario no es válido.';
          } else if (reason === 'email_not_verified') {
            msg += ' El correo electrónico no ha sido verificado.';
          } else if (reason === 'user_inactive') {
            msg += ' El usuario no está activo en nuestro sistema.';
          }
          this.isProcessingMicrosoftLogin = false;
          this.popupInfoService.showError(msg);
          // Limpiar el query param para evitar mostrar el error repetidamente
          this.router
            .navigate([], {
              queryParams: { access_token: null, reason: null },
              queryParamsHandling: 'merge',
            })
            .catch(() => {
              // Ignorar error de navegación
            });
        } else {
          this.handleTokenLogin(accessToken);
        }
      }
    });
  }

  submit(): void {
    this.errorMessage = '';

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { email, password } = this.form.getRawValue();

    this.isLoading = true;
    this.auth.login(email, password).subscribe({
      next: () => {
        this.isLoading = false;
        this.router.navigateByUrl('/dashboard').catch(() => {
          // Ignorar error de navegación
        });
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Error al iniciar sesión, por favor inténtalo de nuevo más tarde.';
      },
    });
  }

  openForgot(): void {
    this.forgotMessage = '';
    this.forgotForm.reset({ email: '' });
    this.showForgot = true;
  }

  closeForgot(): void {
    this.showForgot = false;
  }

  sendForgot(): void {
    this.forgotMessage = '';
    if (this.forgotForm.invalid) {
      this.forgotForm.markAllAsTouched();
      return;
    }
    const email = this.forgotForm.getRawValue().email;
    this.isSendingForgot = true;

    this.auth.forgotPassword(email).subscribe({
      next: (response) => {
        this.isSendingForgot = false;
        this.closeForgot();
        const successMessage =
          response.message || 'Se ha enviado un enlace de recuperación a tu correo.';
        this.popupInfoService.showSuccess(successMessage);
      },
      error: () => {
        this.isSendingForgot = false;
        const errorMessage =
          'Error al enviar el correo de recuperación, por favor vuelve a intentarlo más tarde.';
        this.popupInfoService.showError(errorMessage);
      },
    });
  }

  handleTokenLogin(accessToken: string) {
    // Microsoft login: persistir sesión siempre (localStorage)
    this.auth.setAccessTokenMicrosoft(accessToken);
    this.auth.me().subscribe({
      next: () => {
        this.router.navigateByUrl('/dashboard').then(() => {
          this.isProcessingMicrosoftLogin = false;
        });
      },
      error: () => {
        const errorMessage =
          'Error al validar el token de Microsoft, por favor intenta iniciar sesión más tarde.';
        this.popupInfoService.showError(errorMessage);
        this.isProcessingMicrosoftLogin = false;
      },
    });
  }

  loginWithMicrosoft(event?: Event) {
    // Prevenir cualquier validación del formulario principal
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.isMicrosoftLoginLoading = true;
    this.auth.loginMicrosoft().subscribe({
      next: (response) => {
        window.location.href = response.url;
      },
      error: () => {
        this.isMicrosoftLoginLoading = false;
        const errorMessage =
          'No se pudo iniciar sesión con Microsoft, por favor inténtalo de nuevo más tarde.';
        this.popupInfoService.showError(errorMessage);
      },
    });
  }

  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }
}
